<script setup lang="ts">
/**
 * Heatmap: rows × columns of cells, shaded in four steps of one hue by quartile
 * (a few outliers never wash the rest out). A cell has three states: no entry
 * (`undefined` — a plain empty cell), an entry without a value (`null` — a
 * hatched cell) and a value (shaded).
 *
 * Hovering a cell shows row, column and value, and marks its row and column
 * labels. Column labels thin out when the columns get narrow. The „weniger …
 * mehr“ key sits under the grid. Screen readers get a hidden table.
 *
 * `interactive` turns the cells into buttons — a picker rather than a chart,
 * e.g. the free slots of a team: one tab stop, the arrow keys (Home/End) move
 * between cells, Enter/Space or a click emit `cell-click`. Each cell is named
 * by `cellLabel` (also its hover title); the hidden table and the live region
 * give way to those names. `tone` colours a cell by intent (`success`,
 * `warning`, `danger` …) instead of the quartile shades, `showValues` prints
 * `formatValue` in the cell (an empty string leaves it blank; a value too wide
 * for its cell is left out), `bare` drops
 * the row and column labels and the key for a strip whose axis is drawn
 * around it, and `columnGroups` splits the columns into runs of that many
 * cells with a rule between them, so a run lines up with a column of the
 * grid around it (a day of a timeline).
 */
import { computed, inject, ref, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import type { NxAccentTone } from '../Panel/accent';
import { thinLabels } from './frame';
import { heatLevels, type NxHeatLevel } from './heat';
import { announceTooltip } from './announce';
import NxChartLive from './NxChartLive.vue';
import NxChartTable from './NxChartTable.vue';
import NxChartTooltip from './NxChartTooltip.vue';
import { useChartSize } from './useChartSize';

export interface NxHeatmapRow {
  key: string | number;
  label: string;
  /** One entry per column: a value, `null` (entry without value) or `undefined` (no entry). */
  values: readonly (number | null | undefined)[];
}

const props = withDefaults(
  defineProps<{
    columns: readonly string[];
    rows: readonly NxHeatmapRow[];
    ariaLabel?: string;
    formatValue?: (value: number) => string;
    /** Key captions and cell texts. */
    lessLabel?: string;
    moreLabel?: string;
    emptyLabel?: string;
    noValueLabel?: string;
    locale?: string;
    /** Spoken hint for keyboard users, appended to the grid's name. */
    keyboardHint?: string;
    /** Cells are buttons with a roving focus and emit `cell-click`. */
    interactive?: boolean;
    /** Accessible name and title of an interactive cell. Default: „row · column: value“. */
    cellLabel?: (row: NxHeatmapRow, columnIndex: number) => string;
    /** Intent colour of a valued cell instead of the quartile shade. */
    tone?: (value: number, row: NxHeatmapRow, columnIndex: number) => NxAccentTone | undefined;
    /** Prints the formatted value inside each valued cell. */
    showValues?: boolean;
    /** Cells only: no row and column labels, no key. */
    bare?: boolean;
    /** Cells per run; runs get a rule between them (a day of slots). */
    columnGroups?: number;
  }>(),
  {
    ariaLabel: undefined,
    formatValue: undefined,
    lessLabel: 'Weniger',
    moreLabel: 'Mehr',
    emptyLabel: 'Kein Eintrag',
    noValueLabel: 'Ohne Wert',
    locale: undefined,
    keyboardHint: 'Pfeiltasten wechseln die Zelle',
    interactive: false,
    cellLabel: undefined,
    tone: undefined,
    showValues: false,
    bare: false,
    columnGroups: 0,
  }
);

const emit = defineEmits<{
  /** An interactive cell was clicked or pressed (Enter/Space). */
  'cell-click': [row: NxHeatmapRow, columnIndex: number];
}>();

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);
const numberFormat = computed(() => new Intl.NumberFormat(locale.value));
function format(value: number): string {
  return props.formatValue ? props.formatValue(value) : numberFormat.value.format(value);
}

type Cell =
  | { kind: 'absent' }
  | { kind: 'null' }
  | { kind: 'valued'; value: number; level: NxHeatLevel; tone?: NxAccentTone };

const level = computed(() => {
  const all: number[] = [];
  for (const r of props.rows) {
    for (const v of r.values) if (typeof v === 'number' && Number.isFinite(v)) all.push(v);
  }
  return heatLevels(all);
});

const grid = computed(() =>
  props.rows.map((r) => ({
    key: r.key,
    label: r.label,
    source: r,
    cells: props.columns.map((_, ci): Cell => {
      const v = r.values[ci];
      if (v === undefined) return { kind: 'absent' };
      if (v === null || !Number.isFinite(v)) return { kind: 'null' };
      return { kind: 'valued', value: v, level: level.value(v), tone: props.tone?.(v, r, ci) };
    }),
  }))
);

const colsRef = useTemplateRef<HTMLElement>('cols');
const { width } = useChartSize(colsRef, { width: 360, height: 160 });
const colStep = computed(() => width.value / Math.max(1, props.columns.length));
const colLabels = computed(() => {
  const shown = new Map(thinLabels(props.columns, colStep.value).map((l) => [l.index, l.text]));
  return props.columns.map((_, i) => shown.get(i) ?? '');
});

const active = ref<{ r: number; c: number } | null>(null);
const rootRef = useTemplateRef<HTMLElement>('root');
const anchor = ref({ x: 0, y: 0, w: 0 });

function enter(r: number, c: number, event: PointerEvent) {
  active.value = { r, c };
  const root = rootRef.value?.getBoundingClientRect();
  const cell = (event.currentTarget as HTMLElement).getBoundingClientRect();
  if (!root) return;
  anchor.value = {
    x: cell.left - root.left + cell.width / 2,
    y: cell.top - root.top + cell.height / 2,
    w: root.width,
  };
}

/** Tooltip anchor at the middle of cell (r, c) (keyboard). */
function anchorCell(r: number, c: number) {
  const root = rootRef.value;
  const cell = root?.querySelector<HTMLElement>(`[data-cell="${r}-${c}"]`);
  if (!root || !cell) return;
  const box = root.getBoundingClientRect();
  const rect = cell.getBoundingClientRect();
  anchor.value = {
    x: rect.left - box.left + rect.width / 2,
    y: rect.top - box.top + rect.height / 2,
    w: box.width,
  };
}

function onKeydown(event: KeyboardEvent) {
  const rowsN = grid.value.length;
  const colsN = props.columns.length;
  if (!rowsN || !colsN) return;
  const cur = active.value ?? { r: 0, c: 0 };
  let { r, c } = cur;
  switch (event.key) {
    case 'ArrowRight':
      c = Math.min(c + 1, colsN - 1);
      break;
    case 'ArrowLeft':
      c = Math.max(c - 1, 0);
      break;
    case 'ArrowDown':
      r = Math.min(r + 1, rowsN - 1);
      break;
    case 'ArrowUp':
      r = Math.max(r - 1, 0);
      break;
    case 'Home':
      c = 0;
      break;
    case 'End':
      c = colsN - 1;
      break;
    case 'Escape':
      active.value = null;
      return;
    default:
      return;
  }
  event.preventDefault();
  // The first key press only shows where the walk starts.
  if (!active.value) ({ r, c } = cur);
  active.value = { r, c };
  anchorCell(r, c);
}

function cellText(cell: Cell): string {
  if (cell.kind === 'absent') return props.emptyLabel;
  if (cell.kind === 'null') return props.noValueLabel;
  return format(cell.value);
}

const tooltip = computed(() => {
  const a = active.value;
  if (!a) return null;
  const row = grid.value[a.r];
  const cell = row?.cells[a.c];
  if (!row || !cell) return null;
  return {
    title: `${row.label} · ${props.columns[a.c] ?? ''}`,
    rows: [{ key: 0, label: '', value: cellText(cell), muted: cell.kind !== 'valued' }],
  };
});

/** What the live region says: only while the grid has keyboard focus. */
const focused = ref(false);
const announcement = computed(() => (focused.value ? announceTooltip(tooltip.value) : ''));
const gridLabel = computed(() => [props.ariaLabel, props.keyboardHint].filter(Boolean).join('. '));

/** Column indices in runs of `columnGroups` (one run without it). */
const groups = computed(() => {
  const size = Math.floor(props.columnGroups);
  const all = props.columns.map((_, ci) => ci);
  if (!(size > 0)) return [all];
  const runs: number[][] = [];
  for (let i = 0; i < all.length; i += size) runs.push(all.slice(i, i + size));
  return runs;
});

function cellClasses(cell: Cell, ri: number, ci: number) {
  return [
    cell.kind !== 'valued'
      ? `is-${cell.kind}`
      : cell.tone
        ? `tone-${cell.tone}`
        : `level-${cell.level}`,
    { 'is-active': active.value?.r === ri && active.value?.c === ci },
  ];
}

function cellValueText(cell: Cell): string {
  return props.showValues && cell.kind === 'valued' ? format(cell.value) : '';
}

/** The interactive cell that holds the tab stop. */
const current = ref({ r: 0, c: 0 });

function nameOf(ri: number, ci: number): string {
  const row = grid.value[ri];
  const cell = row?.cells[ci];
  if (!row || !cell) return '';
  if (props.cellLabel) return props.cellLabel(row.source, ci);
  return `${row.label} · ${props.columns[ci] ?? ''}: ${cellText(cell)}`;
}

function focusCell(r: number, c: number) {
  current.value = { r, c };
  rootRef.value?.querySelector<HTMLElement>(`[data-cell="${r}-${c}"]`)?.focus();
}

function onCellKeydown(event: KeyboardEvent, ri: number, ci: number) {
  const last = props.columns.length - 1;
  const lastRow = grid.value.length - 1;
  let { r, c } = { r: ri, c: ci };
  switch (event.key) {
    case 'ArrowRight':
      c = Math.min(c + 1, last);
      break;
    case 'ArrowLeft':
      c = Math.max(c - 1, 0);
      break;
    case 'ArrowDown':
      r = Math.min(r + 1, lastRow);
      break;
    case 'ArrowUp':
      r = Math.max(r - 1, 0);
      break;
    case 'Home':
      c = 0;
      break;
    case 'End':
      c = last;
      break;
    default:
      return;
  }
  event.preventDefault();
  focusCell(r, c);
}

function onCellClick(ri: number, ci: number) {
  current.value = { r: ri, c: ci };
  const row = grid.value[ri];
  if (row) emit('cell-click', row.source, ci);
}

/** Keeps the tab stop on a cell that exists when rows or columns shrink. */
const tabStop = computed(() => ({
  r: Math.min(current.value.r, Math.max(0, grid.value.length - 1)),
  c: Math.min(current.value.c, Math.max(0, props.columns.length - 1)),
}));

const table = computed(() => ({
  columns: ['', ...props.columns],
  rows: grid.value.map((r) => ({ key: r.key, cells: [r.label, ...r.cells.map(cellText)] })),
}));
</script>

<template>
  <div
    ref="root"
    class="nx-heatmap"
    :class="{ 'is-bare': props.bare, 'is-interactive': props.interactive }"
    role="group"
    :aria-label="props.ariaLabel"
    :style="{
      '--cols': String(Math.max(1, props.columns.length)),
      '--rows': String(Math.max(1, props.rows.length)),
      '--groups': String(groups.length),
      '--group-size': String(Math.max(1, groups[0]?.length ?? 1)),
    }"
  >
    <div
      class="nx-heatmap__focus"
      :tabindex="props.interactive ? undefined : 0"
      :role="props.interactive ? undefined : 'group'"
      :aria-label="props.interactive ? undefined : gridLabel"
      @keydown="!props.interactive && onKeydown($event)"
      @focus="focused = !props.interactive"
      @blur="
        focused = false;
        active = null;
      "
    >
      <div class="nx-heatmap__grid" :aria-hidden="props.interactive ? undefined : 'true'">
        <template v-for="(row, ri) in grid" :key="row.key">
          <span
            v-if="!props.bare"
            class="nx-heatmap__row-label"
            :class="{ 'is-active': active?.r === ri }"
            :title="row.label"
          >
            {{ row.label }}
          </span>
          <div class="nx-heatmap__cells" :class="{ 'has-groups': groups.length > 1 }">
            <div
              v-for="(run, gi) in groups"
              :key="gi"
              class="nx-heatmap__run"
              :class="{ 'is-group': groups.length > 1 }"
            >
              <component
                :is="props.interactive ? 'button' : 'span'"
                v-for="ci in run"
                :key="ci"
                class="nx-heatmap__cell"
                :class="cellClasses(row.cells[ci]!, ri, ci)"
                :type="props.interactive ? 'button' : undefined"
                :tabindex="
                  props.interactive ? (tabStop.r === ri && tabStop.c === ci ? 0 : -1) : undefined
                "
                :aria-label="props.interactive ? nameOf(ri, ci) : undefined"
                :title="props.interactive ? nameOf(ri, ci) : undefined"
                :data-cell="`${ri}-${ci}`"
                :style="{ animationDelay: `${Math.min(ri + ci, 24) * 12}ms` }"
                @pointerenter="!props.interactive && enter(ri, ci, $event)"
                @pointerleave="active = null"
                @click="props.interactive && onCellClick(ri, ci)"
                @keydown="props.interactive && onCellKeydown($event, ri, ci)"
              >
                <span v-if="cellValueText(row.cells[ci]!)" class="nx-heatmap__value">
                  {{ cellValueText(row.cells[ci]!) }}
                </span>
              </component>
            </div>
          </div>
        </template>
        <template v-if="!props.bare">
          <span />
          <div ref="cols" class="nx-heatmap__cols">
            <span
              v-for="(label, ci) in colLabels"
              :key="ci"
              class="nx-heatmap__col-label"
              :class="{ 'is-active': active?.c === ci }"
              :title="props.columns[ci]"
            >
              {{ label }}
            </span>
          </div>
        </template>
      </div>
      <NxChartLive v-if="!props.interactive" :text="announcement" />
    </div>
    <div v-if="!props.bare" class="nx-heatmap__key" aria-hidden="true">
      <span>{{ props.lessLabel }}</span>
      <span class="nx-heatmap__cell is-absent" />
      <span v-for="l in 4" :key="l" class="nx-heatmap__cell" :class="`level-${l}`" />
      <span>{{ props.moreLabel }}</span>
    </div>
    <NxChartTooltip
      v-if="tooltip"
      :x="anchor.x"
      :y="anchor.y"
      :box-width="anchor.w"
      :title="tooltip.title"
      :rows="tooltip.rows"
    />
    <NxChartTable
      v-if="!props.interactive"
      :caption="props.ariaLabel"
      :columns="table.columns"
      :rows="table.rows"
    />
  </div>
</template>

<style scoped>
.nx-heatmap {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  width: 100%;
  height: 100%;
  min-width: 0;
}
.nx-heatmap__focus {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  border-radius: var(--nxt-radius-sm);
  outline: none;
}
.nx-heatmap__focus:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-heatmap__grid {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: minmax(0, max-content) minmax(0, 1fr);
  /* Cell rows share the height; the column-label row keeps its own. */
  grid-template-rows: repeat(var(--rows), minmax(calc(var(--nxt-size-unit) * 0.9), 1fr)) auto;
  gap: calc(var(--nxt-hairline) * 3) var(--nxt-space-sm);
  min-height: 0;
}
.nx-heatmap__row-label {
  align-self: center;
  max-width: calc(var(--nxt-size-unit) * 9);
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-align: end;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-heatmap__row-label.is-active,
.nx-heatmap__col-label.is-active {
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-heatmap__cells,
.nx-heatmap__cols {
  display: grid;
  grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
  gap: calc(var(--nxt-hairline) * 3);
  min-width: 0;
}
.nx-heatmap__run {
  display: contents;
}
/* Runs of `columnGroups`: equal shares of the width (like the columns of the
   grid they line up with), a rule at the end of each, tight cells inside. */
.nx-heatmap__cells.has-groups {
  grid-template-columns: repeat(var(--groups), minmax(0, 1fr));
  gap: 0;
}
.nx-heatmap__run.is-group {
  display: grid;
  grid-template-columns: repeat(var(--group-size), minmax(0, 1fr));
  gap: var(--nxt-hairline);
  min-width: 0;
  padding: var(--nxt-space-2xs);
  border-inline-end: var(--nxt-hairline) solid var(--nxt-border);
}
.has-groups .nx-heatmap__cell {
  min-width: 0;
}
.is-bare .nx-heatmap__grid {
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: repeat(var(--rows), minmax(calc(var(--nxt-size-unit) * 0.9), 1fr));
}
.nx-heatmap__cell {
  display: block;
  min-width: calc(var(--nxt-size-unit) * 0.5);
  min-height: calc(var(--nxt-size-unit) * 0.75);
  border-radius: calc(var(--nxt-hairline) * 3);
  background: var(--nxt-chart-track);
  transition: box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
  animation: nx-heat-in var(--nxt-motion-slow) var(--nxt-ease-out) both;
}
.nx-heatmap__cell.level-1 {
  background: var(--nxt-chart-seq-1);
}
.nx-heatmap__cell.level-2 {
  background: var(--nxt-chart-seq-2);
}
.nx-heatmap__cell.level-3 {
  background: var(--nxt-chart-seq-3);
}
.nx-heatmap__cell.level-4 {
  background: var(--nxt-chart-seq-4);
}
/* Intent colours (`tone`): the tone's fill, lightened so the value stays legible. */
.nx-heatmap__cell.tone-brand {
  --heat-tone: var(--nxt-brand-bg);
}
.nx-heatmap__cell.tone-neutral {
  --heat-tone: var(--nxt-neutral-bg);
}
.nx-heatmap__cell.tone-info {
  --heat-tone: var(--nxt-info-bg);
}
.nx-heatmap__cell.tone-success {
  --heat-tone: var(--nxt-success-bg);
}
.nx-heatmap__cell.tone-warning {
  --heat-tone: var(--nxt-warning-bg);
}
.nx-heatmap__cell.tone-danger {
  --heat-tone: var(--nxt-danger-bg);
}
.nx-heatmap__cell[class*='tone-'] {
  background: color-mix(in srgb, var(--heat-tone) 60%, transparent);
}
/* Interactive cells are buttons; the printed value sits in the middle. */
button.nx-heatmap__cell {
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  line-height: 1;
  cursor: pointer;
}
/* A value too wide for its cell is left out; the cell's name still says it. */
.nx-heatmap__cell:has(.nx-heatmap__value) {
  container-type: inline-size;
}
@container (width < 1.5em) {
  .nx-heatmap__value {
    display: none;
  }
}
button.nx-heatmap__cell:hover {
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-fg-muted);
}
button.nx-heatmap__cell:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
/* No value: fine hatching, three hairlines of track to one of baseline. */
.nx-heatmap__cell.is-null {
  background: repeating-linear-gradient(
    135deg,
    var(--nxt-chart-track) 0 calc(var(--nxt-hairline) * 3),
    var(--nxt-chart-baseline) calc(var(--nxt-hairline) * 3) calc(var(--nxt-hairline) * 4)
  );
}
/* The picked cell: a surface gap, then a ring of the text colour around it. */
.nx-heatmap__grid .nx-heatmap__cell.is-active {
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg),
    0 0 0 calc(var(--nxt-hairline) * 3.5) var(--nxt-fg);
}
.nx-heatmap__col-label {
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-align: center;
  white-space: nowrap;
}
.nx-heatmap__key {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: calc(var(--nxt-hairline) * 3);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-heatmap__key .nx-heatmap__cell {
  width: var(--nxt-dot-size-lg);
  height: var(--nxt-dot-size-lg);
  min-width: 0;
  min-height: 0;
  animation: none;
}
.nx-heatmap__key > span:first-child {
  margin-inline-end: var(--nxt-space-2xs);
}
.nx-heatmap__key > span:last-child {
  margin-inline-start: var(--nxt-space-2xs);
}
@keyframes nx-heat-in {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-heatmap__cell {
    animation: none;
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-heatmap__cell {
    forced-color-adjust: none;
  }
}
</style>
